import { CustomPillToggle } from '../../../shared/components/customPillToggle';
import { Box, Typography, Modal } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import InfoIcon from '../../../assets/icons/infoIcon.svg';
import { ArcContainer } from '../../../shared/components/arcContainer';
import loosePantsIcon from '../../../assets/icons/loosePantsIcon.svg';
import notHungryIcon from '../../../assets/icons/notHungryIcon.svg';
import { TextWithInfoIcon } from '../../../shared/components/textWithInfoIcon';
import { BoxStyled } from './style';
import { NavigationButtons } from '../../../shared/components/navigationButtons';
import FormControlLabel, {
  FormControlLabelProps
} from '@mui/material/FormControlLabel';
import RadioGroup, { useRadioGroup } from '@mui/material/RadioGroup';
import Radio from '@mui/material/Radio';
import { Tooltip } from '../../../shared/components/toolTip/';

import { useDispatch } from 'react-redux';
import { useAppSelector } from '../../../store/hooks';
import { metaDataSelector } from '../../../store/globalSelectors';
import { setMSTSelection } from '../../../store/slices/metaDataSlice';

interface StyledFormControlLabelProps extends FormControlLabelProps {
  checked: boolean;
}

const StyledFormControlLabel = styled((props: StyledFormControlLabelProps) => (
  <FormControlLabel {...props} />
))(({ theme }) => ({
  variants: [
    {
      props: { checked: true || false },
      style: {
        '.MuiFormControlLabel-label': {
          color: theme.palette.primary.main,
          fontSize: '1rem',
          lineHeight: '1.25rem',
          fontFamily: 'Calibri'
        }
      }
    }
  ]
}));

function MyFormControlLabel(props: FormControlLabelProps) {
  const radioGroup = useRadioGroup();

  let checked = false;

  if (radioGroup) {
    checked = radioGroup.value === props.value;
  }

  return <StyledFormControlLabel checked={checked} {...props} />;
}

export const Mst = () => {
  let radioBtnVal = 0;
  const dispatch = useDispatch();
  const { malnutritionScreening } = useAppSelector(metaDataSelector);
  const { mst } = malnutritionScreening;
  const [checkedVal1, setCheckedVal1] = useState(mst.unintentionalWeightLoss);
  const [checkedVal2, setCheckedVal2] = useState(mst.reducedFoodIntake);

  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleSlider1Click = () => {
    setCheckedVal1(!checkedVal1);
  };

  const handleSlider2Click = () => {
    setCheckedVal2(!checkedVal2);
  };

  const handleRadioBtnSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (checkedVal1) {
      if (event.target.value === '1-5kg') radioBtnVal = 1;
      else if (event.target.value === '6-10kg') radioBtnVal = 2;
      else if (event.target.value === '11-15kg') radioBtnVal = 3;
      else if (event.target.value === '>15kg') radioBtnVal = 4;
      else radioBtnVal = 2;

      dispatch(
        setMSTSelection({
          ...mst,
          weightLoss: {
            ...mst.weightLoss,
            value: radioBtnVal,
            optionSelected: event.target.value
          }
        })
      );
    }
  };

  useEffect(() => {
    const unintentionalWeightLoss = checkedVal1 ? 1 : 0;
    const reducedFoodIntake = checkedVal2 ? 1 : 0;

    // Calculate total score
    const totalScore = unintentionalWeightLoss
      ? mst.weightLoss.value + reducedFoodIntake
      : reducedFoodIntake;

    dispatch(
      setMSTSelection({
        ...mst,
        reducedFoodIntake,
        unintentionalWeightLoss,
        totalScore
      })
    );
  }, [checkedVal1, checkedVal2, mst.weightLoss.value]);

  return (
    <>
      <BoxStyled>
        {/* Top Bar (title + info icon) */}
        <TextWithInfoIcon
          data-testid={'mstInfoPopupButton'}
          title={'MST'}
          icon={InfoIcon}
          fontSize={'1.5rem'}
          lineHeight={'1.375rem'}
          variant={'h1'}
          handleIconClick={handleOpen}
        />
        <ArcContainer icon={loosePantsIcon} customizedHeight="auto">
          <Box
            sx={{
              margin: '1.25rem 0'
            }}
          >
            <Typography
              sx={{
                color: '#002A3A',
                fontFamily: 'BGBold',
                fontSize: '1.125rem',
                lineHeight: '1.5rem',
                textAlign: 'center'
              }}
              className="pt-16"
            >
              WEIGHT LOSS
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              width: '100%', // or a fixed width if needed
              mx: 'auto', // center horizontally if desired
              margin: '1.25rem 0'
            }}
          >
            <Box sx={{ width: '300px' }}>
              <Typography
                sx={{
                  color: '#002A3A',
                  fontFamily: 'Calibri',
                  fontSize: '1rem'
                }}
              >
                Has your patient experienced unintentional weight loss in the
                past 6 months?
              </Typography>
            </Box>

            {/* Right: the custom pill toggle */}
            <CustomPillToggle
              handleClick={handleSlider1Click}
              checked={mst.unintentionalWeightLoss}
              truthyValue="YES"
              falsyValue="NO"
            />
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexDirection: 'column',
              margin: '0.9375rem'
            }}
          >
            <Typography
              sx={{
                color: '#002A3A',
                fontFamily: 'BGBold',
                fontSize: '0.875rem',
                lineHeight: '0.9375rem',
                textAlign: 'center',
                marginBottom: '0.9375rem'
              }}
            >
              HOW MUCH WEIGHT?
            </Typography>
            <Box>
              <RadioGroup
                sx={{ height: '150px', gap: '0.625rem' }}
                name="how-much-weight"
                onChange={handleRadioBtnSelect}
                value={
                  mst.unintentionalWeightLoss
                    ? mst.weightLoss.optionSelected
                    : null
                }
              >
                <MyFormControlLabel
                  value="1-5kg"
                  label={
                    <Typography style={{ color: '#002A3A' }}>1-5kg</Typography>
                  }
                  control={<Radio />}
                  disabled={!checkedVal1}
                />
                <MyFormControlLabel
                  value="6-10kg"
                  label={<Typography>6-10kg</Typography>}
                  control={<Radio />}
                  disabled={!checkedVal1}
                />
                <MyFormControlLabel
                  value="11-15kg"
                  label={<Typography>11-15kg</Typography>}
                  control={<Radio />}
                  disabled={!checkedVal1}
                />
                <MyFormControlLabel
                  value=">15kg"
                  label={<Typography>{`>15kg`}</Typography>}
                  control={<Radio />}
                  disabled={!checkedVal1}
                />
                <MyFormControlLabel
                  value="Unsure"
                  label={<Typography>Unsure</Typography>}
                  control={<Radio />}
                  disabled={!checkedVal1}
                />
              </RadioGroup>
            </Box>
          </Box>
        </ArcContainer>
        {/* <Box sx={{ minHeight: 'auto' }}> */}
        <ArcContainer icon={notHungryIcon} customizedHeight="auto">
          <Box
            sx={{
              margin: '1.25rem 0'
            }}
          >
            <Typography
              sx={{
                color: '#002A3A',
                fontFamily: 'BGBold',
                fontSize: '1.125rem',
                lineHeight: '1.5rem',
                textAlign: 'center'
              }}
              className="pt-16"
            >
              REDUCED FOOD INTAKE
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              width: '100%', // or a fixed width if needed
              mx: 'auto', // center horizontally if desired
              margin: '1.25rem 0'
            }}
          >
            <Box sx={{ width: '300px' }}>
              <Typography
                sx={{
                  color: '#002A3A',
                  fontFamily: 'Calibri',
                  fontSize: '1rem'
                }}
              >
                Does your patient have reduced food intake secondary to poor
                appetite?
              </Typography>
            </Box>

            {/* Right: the custom pill toggle */}
            <CustomPillToggle
              handleClick={handleSlider2Click}
              checked={mst.reducedFoodIntake}
              truthyValue="YES"
              falsyValue="NO"
            />
          </Box>
        </ArcContainer>
        {/* </Box> */}
      </BoxStyled>

      <NavigationButtons
        nextDisabled={false}
        nextRoute={'/malnutrition-screening/mst/screening-result'}
      />
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Tooltip
          onClose={handleClose}
          title={'Reference:'}
          content={
            <Typography
              align="left"
              sx={{
                fontFamily: 'Calibri',
                fontSize: '1rem',
                fontWeight: 400,
                lineHeight: '1.25rem',
                color: '#004F71'
              }}
            >
              Ferguson, M
              <span style={{ fontFamily: 'CalibriItalic' }}> et al.</span> Clin
              Nutrition 1999 15:458-464.
            </Typography>
          }
        />
      </Modal>
    </>
  );
};
